import { apiRequest } from '../api';
import { getLocale, onLocaleChange, t } from '../i18n';
import type { TranslationKey } from '../i18n/types';
import { navigateTo } from '../router';
import { buildRadarSVG } from '../radar';
import type { Race } from '../types';
import terranIconUrl from '../../assets/race/terran.svg';
import zergIconUrl from '../../assets/race/zerg.svg';
import protossIconUrl from '../../assets/race/protoss.svg';
import randomIconUrl from '../../assets/race/random.svg';

const RACE_ICON: Record<Race, string> = {
    T: terranIconUrl,
    Z: zergIconUrl,
    P: protossIconUrl,
    R: randomIconUrl,
};

const RACE_LABELS: Record<Race, string> = {
    T: 'Terran',
    Z: 'Zerg',
    P: 'Protoss',
    R: 'Random',
};

// Учитываем только конкретные расы (Random не входит в статистику).
const RACES: Race[] = ['T', 'Z', 'P'];
const raceOrder = (race: string): number => RACES.indexOf(race as Race);

interface RaceGameRow {
    race: string;
    games: number;
    wins: number;
    winrate: number;
    avg_place: number | null;
    activity: number;
    quality: number;
    players: number;
    expected_wins: number;
    power_index: number | null;
    power_index_recent: number | null;
}

interface RaceStyleRow {
    race: string;
    ratings: number;
    adaptiveness: number | null;
    greed: number | null;
    survival: number | null;
    turtle: number | null;
    aggression: number | null;
    variety: number | null;
}

interface RefStatRow {
    name: string;
    id: number | null;
    player_name: string | null;
    games: number;
    players: number;
    avg_players: number;
    avg_duration: number | null;
    last_played_at: string | null;
    race_winrate: { T: number | null; Z: number | null; P: number | null };
    rated_games: number;
    favorite_winrate: number | null;
}

/** Точка временного ряда расы: null — расу ещё нельзя оценить на этот момент. */
interface RaceSeriesPoint {
    t: string;
    T: number | null;
    Z: number | null;
    P: number | null;
}

/** Число игр за месяц (month — YYYY-MM). */
interface MonthGamesRow {
    month: string;
    games: number;
}

/** Число уникальных игроков расы за месяц (month — YYYY-MM). */
interface MonthRacePlayersRow {
    month: string;
    T: number;
    Z: number;
    P: number;
}

/** Винрейт расы, когда её в лобби 1 / 2 / 3+ (own count). */
interface LobbyBucketRow {
    key: '1' | '2' | '3';
    games: number;
    wins: number;
    winrate: number;
}

interface LobbyRaceRow {
    race: string;
    buckets: LobbyBucketRow[];
}

/** Винрейт и среднее место расы в лобби на N игроков. */
interface PlayersCountRow {
    players: number;
    games: number;
    wins: number;
    winrate: number;
    avg_place: number | null;
}

interface RaceByPlayersRow {
    race: string;
    rows: PlayersCountRow[];
}

/** Доля финишей на месте N (в процентах от игр расы). */
interface PlaceShareRow {
    place: number;
    T: number | null;
    Z: number | null;
    P: number | null;
}

/** Первые выбывания и дожитие до финала (place = 2). */
interface FirstElimRow {
    race: string;
    games: number;
    first: number;
    first_share: number;
    last: number;
    last_share: number;
}

interface MetaStats {
    lobby_race: LobbyRaceRow[];
    race_by_players: RaceByPlayersRow[];
    place_distribution: PlaceShareRow[];
    first_elim: FirstElimRow[];
    /** 7 дней (Пн→Вс) × 24 часа по МСК. */
    heatmap: { cells: number[][]; max: number };
    /** Сводка по лиге для KPI-полосы. */
    summary?: {
        games: number;
        players: number;
        maps: number;
        avg_players: number;
        hours: number;
    };
}

interface StatisticsResponse {
    games: RaceGameRow[];
    style: RaceStyleRow[];
    maps: RefStatRow[];
    mods: RefStatRow[];
    hosts: RefStatRow[];
    race_mmr: { base: number; points: RaceSeriesPoint[] };
    race_power: RaceSeriesPoint[];
    race_power_recent: RaceSeriesPoint[];
    games_by_month: MonthGamesRow[];
    players_by_month: MonthRacePlayersRow[];
    meta: MetaStats;
}

type RefSectionKey = 'maps' | 'mods' | 'hosts';
type RefSortKey = 'name' | 'games' | 'players' | 'avg_players' | 'avg_duration' | 'last_played_at' | 'race_T' | 'race_Z' | 'race_P' | 'favorite_winrate';

/** Сколько строк справочника показываем сразу; остальные — под раскрытием. */
const REF_VISIBLE = 10;

type SortDir = 'asc' | 'desc';

const refSort: Record<RefSectionKey, { key: RefSortKey; dir: SortDir }> = {
    maps: { key: 'games', dir: 'desc' },
    mods: { key: 'games', dir: 'desc' },
    hosts: { key: 'games', dir: 'desc' },
};
// Состояние раскрытия «показать все» для карт и мод (ведущие показываются целиком).
const refExpanded: Record<'maps' | 'mods', boolean> = { maps: false, mods: false };

// Режим отображения баланса: «за всё время» / «сейчас» (последние игры).
// Баланс рас и график имбовости переключаются независимо, по умолчанию оба — «сейчас».
type BalanceMode = 'all' | 'now';
let balanceMode: BalanceMode = 'now';
let powerMode: BalanceMode = 'now';

let cache: StatisticsResponse | null = null;
let abortController: AbortController | null = null;

/** Перерисовывает статистику из кэша (для переключателей и сортировки). */
function rerenderStatistics(): void {
    const container = document.getElementById('statistics-container');
    if (cache && container) renderStatistics(cache, container);
}

export function mountStatistics(): void {
    const screen = document.getElementById('screen-statistics');
    if (screen) screen.classList.remove('hidden');

    abortController = new AbortController();
    void loadStatistics();
}

export function unmountStatistics(): void {
    abortController?.abort();
    abortController = null;
}

// Живой пересчёт текста при смене языка.
onLocaleChange(() => {
    const container = document.getElementById('statistics-container');
    if (!container || !cache) return;
    renderStatistics(cache, container);
});

async function loadStatistics(): Promise<void> {
    const container = document.getElementById('statistics-container');
    if (!container) return;

    container.innerHTML = '<div class="skeleton skeleton-block" style="height:400px"></div>';

    try {
        const res = await apiRequest<StatisticsResponse>('/api/statistics');
        cache = res;
        renderStatistics(res, container);
    } catch {
        container.innerHTML = `<p class="error">${t('common.error')}</p>`;
    }
}

function renderStatistics(data: StatisticsResponse, container: HTMLElement): void {
    container.innerHTML = '';

    const main = document.createElement('div');
    main.className = 'statistics-main';

    const baseline = new Map<string, number>();
    for (const g of data.games) baseline.set(g.race, g.winrate);

    main.appendChild(buildSummaryStrip(data.meta?.summary));
    main.appendChild(buildBalanceSection(data.games));
    main.appendChild(buildRacePowerSection(data.race_power, data.race_power_recent));
    main.appendChild(buildRaceMmrSection(data.race_mmr));
    main.appendChild(buildGamesSection(data.games));
    main.appendChild(buildPlayersCountSection(data.meta?.race_by_players ?? [], baseline));
    main.appendChild(buildLobbyRaceSection(data.meta?.lobby_race ?? [], baseline));
    main.appendChild(buildPlaceDistSection(data.meta?.place_distribution ?? []));
    main.appendChild(buildFirstElimSection(data.meta?.first_elim ?? []));
    main.appendChild(buildHeatmapSection(data.meta?.heatmap ?? { cells: [], max: 0 }));
    main.appendChild(buildGamesByMonthSection(data.games_by_month));
    main.appendChild(buildRacePlayersSection(data.players_by_month));
    main.appendChild(buildStyleSection(data.style));
    main.appendChild(buildRefSection('maps', t('statistics.maps_title'), data.maps, true));
    main.appendChild(buildRefSection('mods', t('statistics.mods_title'), data.mods, true));
    main.appendChild(buildRefSection('hosts', t('statistics.hosts_title'), data.hosts, false));

    container.appendChild(main);
}

// --- Ячейка расы: полноцветная иконка + название в цвете расы ---
function buildRaceCell(race: string): HTMLTableCellElement {
    const td = document.createElement('td');
    td.className = 'statistics-race';

    const wrap = document.createElement('span');
    wrap.className = `statistics-race-name race-${race}`;

    const img = document.createElement('img');
    img.src = RACE_ICON[race as Race];
    img.alt = race;
    img.className = 'statistics-race-icon';

    const name = document.createElement('span');
    name.className = 'statistics-race-label';
    name.textContent = RACE_LABELS[race as Race];

    wrap.append(img, name);
    td.appendChild(wrap);
    return td;
}

// --- Сортируемый заголовок (label — строка или иконка-элемент) ---
function buildSortableTh(
    label: string | HTMLElement,
    active: boolean,
    dir: SortDir,
    onClick: () => void
): HTMLTableCellElement {
    const th = document.createElement('th');
    th.className = 'sortable';
    if (typeof label === 'string') th.textContent = label;
    else th.appendChild(label);

    if (active) {
        th.classList.add('is-sorted');
        th.classList.toggle('is-sorted-asc', dir === 'asc');
        th.classList.toggle('is-sorted-desc', dir === 'desc');
        const arrow = document.createElement('span');
        arrow.className = 'statistics-sort-arrow';
        arrow.textContent = dir === 'asc' ? ' ▲' : ' ▼';
        th.appendChild(arrow);
    }
    th.addEventListener('click', onClick);
    return th;
}

function handleSort<T extends string>(
    current: { key: T; dir: SortDir },
    key: T,
    isText: boolean,
    apply: (next: { key: T; dir: SortDir }) => void
): void {
    if (current.key === key) {
        apply({ key, dir: current.dir === 'asc' ? 'desc' : 'asc' });
    } else {
        apply({ key, dir: isText ? 'asc' : 'desc' });
    }
}

/** Сравнение значений: null/undefined всегда в конец, строки — по алфавиту. */
function compareValues(a: number | string | null, b: number | string | null, dir: SortDir): number {
    const sign = dir === 'asc' ? 1 : -1;
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b, 'ru') * sign;
    return (Number(a) - Number(b)) * sign;
}

// ============================================================
// KPI-полоса: общие цифры лиги
// ============================================================
function buildSummaryStrip(summary?: MetaStats['summary']): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'statistics-kpi';
    if (!summary) {
        wrap.classList.add('hidden');
        return wrap;
    }

    const items: Array<{ value: string; label: string }> = [
        { value: fmtInt(summary.games), label: t('statistics.summary_games') },
        { value: fmtInt(summary.players), label: t('statistics.summary_players') },
        { value: fmtInt(summary.maps), label: t('statistics.summary_maps') },
        { value: summary.avg_players.toFixed(1), label: t('statistics.summary_avg_players') },
        { value: fmtInt(summary.hours), label: t('statistics.summary_hours') },
    ];

    for (const item of items) {
        const card = document.createElement('div');
        card.className = 'statistics-kpi-card';
        const value = document.createElement('span');
        value.className = 'statistics-kpi-value';
        value.textContent = item.value;
        const label = document.createElement('span');
        label.className = 'statistics-kpi-label';
        label.textContent = item.label;
        card.append(value, label);
        wrap.appendChild(card);
    }
    return wrap;
}

function fmtInt(n: number): string {
    return n.toLocaleString(getLocale() === 'en' ? 'en-US' : 'ru-RU');
}

/** Иконка + название расы в цвете расы (для карточек). */
function buildRaceTag(race: string): HTMLElement {
    const tag = document.createElement('span');
    tag.className = `statistics-race-name race-${race}`;

    const img = document.createElement('img');
    img.src = RACE_ICON[race as Race];
    img.alt = race;
    img.className = 'statistics-race-icon';

    const name = document.createElement('span');
    name.textContent = RACE_LABELS[race as Race];

    tag.append(img, name);
    return tag;
}

// --- Раздел: игровая статистика по расам ---
function buildGamesSection(rows: RaceGameRow[]): HTMLElement {
    const section = document.createElement('section');
    section.className = 'statistics-section';

    const h2 = document.createElement('h2');
    h2.textContent = t('statistics.games_title');
    section.appendChild(h2);

    if (rows.length === 0) {
        const p = document.createElement('p');
        p.className = 'hint';
        p.textContent = t('statistics.empty');
        section.appendChild(p);
        return section;
    }

    const desc = document.createElement('p');
    desc.className = 'statistics-balance-desc';
    desc.textContent = t('statistics.games_desc');
    section.appendChild(desc);

    const ordered = [...rows].sort((a, b) => raceOrder(a.race) - raceOrder(b.race));
    // Относительные шкалы для полос: место — лучше меньше, остальное — больше.
    const maxPlace = Math.max(1, ...ordered.map((r) => r.avg_place ?? 1));
    const maxActivity = Math.max(0.001, ...ordered.map((r) => r.activity));
    const maxPlayers = Math.max(1, ...ordered.map((r) => r.players));

    const cards = document.createElement('div');
    cards.className = 'statistics-race-cards';

    for (const r of ordered) {
        const card = document.createElement('div');
        card.className = `statistics-race-card race-${r.race}`;

        const head = document.createElement('div');
        head.className = 'statistics-race-card-head';
        head.appendChild(buildRaceTag(r.race));
        const games = document.createElement('span');
        games.className = 'statistics-race-card-games';
        games.textContent = `${fmtInt(r.games)} ${t('leaderboard.games')}`;
        head.appendChild(games);

        const donut = buildWinrateDonut(r.winrate, RACE_SERIES_COLORS[r.race as RaceSeriesKey]);

        const wins = document.createElement('div');
        wins.className = 'statistics-donut-note';
        wins.textContent = `${fmtInt(r.wins)} ${t('leaderboard.wins')}`;

        const metrics = document.createElement('div');
        metrics.className = 'statistics-metric-list';
        const placeRatio = r.avg_place !== null ? 1 - (r.avg_place - 1) / Math.max(1, maxPlace - 1) : 0;
        metrics.append(
            buildMetricBar(t('leaderboard.avg_place'), r.avg_place !== null ? r.avg_place.toFixed(2) : '—', placeRatio, 'is-place'),
            buildMetricBar(t('leaderboard.activity'), r.activity.toFixed(1), r.activity / maxActivity, 'is-activity'),
            buildMetricBar(t('statistics.col_players'), String(r.players), r.players / maxPlayers, 'is-players')
        );

        card.append(head, donut, wins, metrics);
        cards.appendChild(card);
    }

    section.appendChild(cards);
    return section;
}

/** Кольцевая диаграмма винрейта (SVG, без зависимостей). */
function buildWinrateDonut(value: number, color: string): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'statistics-donut';
    const clamped = Math.max(0, Math.min(100, value));
    wrap.innerHTML = `<svg viewBox="0 0 120 120" width="132" height="132" aria-hidden="true">
        <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.08)" stroke-width="10"/>
        <circle cx="60" cy="60" r="52" fill="none" stroke="${color}" stroke-width="10" stroke-linecap="round"
            pathLength="100" stroke-dasharray="${clamped} 100" transform="rotate(-90 60 60)"/>
    </svg>`;

    const center = document.createElement('span');
    center.className = 'statistics-donut-value';
    center.textContent = `${value.toFixed(1)}%`;
    wrap.appendChild(center);
    return wrap;
}

/** Строка метрики: подпись, значение и полоса отношения. */
function buildMetricBar(label: string, value: string, ratio: number, cls = ''): HTMLElement {
    const row = document.createElement('div');
    row.className = `statistics-metric ${cls}`;

    const head = document.createElement('div');
    head.className = 'statistics-metric-head';
    const lab = document.createElement('span');
    lab.className = 'statistics-metric-label';
    lab.textContent = label;
    const val = document.createElement('span');
    val.className = 'statistics-metric-value';
    val.textContent = value;
    head.append(lab, val);

    const track = document.createElement('div');
    track.className = 'statistics-metric-track';
    const fill = document.createElement('span');
    fill.className = 'statistics-metric-fill';
    fill.style.width = `${Math.round(Math.max(0, Math.min(1, ratio)) * 100)}%`;
    track.appendChild(fill);

    row.append(head, track);
    return row;
}

// ============================================================
// Метагейм: число игроков, состав лобби, места, выбывания
// ============================================================

/** Ячейка матрицы: процент крупно + число игр мелким шрифтом, подсветка относительно среднего винрейта расы. */
function makeMatrixTd(
    value: number | null,
    games: number,
    label: string,
    race?: string,
    baseline?: number
): HTMLTableCellElement {
    const td = document.createElement('td');
    td.className = 'statistics-meta-cell';
    if (race) td.classList.add(`race-${race}`);
    // Малая выборка — приглушаем, чтобы не читать шум как сигнал.
    if (games < 5) td.classList.add('is-small');
    td.dataset.label = label;

    // Тепло: зеленее — раса выступает лучше своего среднего винрейта, краснее — хуже.
    if (value !== null && typeof baseline === 'number') {
        const diff = value - baseline;
        const intensity = Math.min(1, Math.abs(diff) / 15);
        const alpha = (0.05 + 0.3 * intensity).toFixed(3);
        td.style.background = diff >= 0
            ? `rgba(46, 204, 113, ${alpha})`
            : `rgba(231, 76, 60, ${alpha})`;
    }

    const val = document.createElement('span');
    val.className = 'statistics-meta-value';
    val.textContent = value !== null ? `${value.toFixed(1)}%` : '—';

    const cnt = document.createElement('span');
    cnt.className = 'statistics-meta-games';
    cnt.textContent = games > 0 ? String(games) : '';

    td.append(val, cnt);
    td.title = `${value !== null ? `${value.toFixed(1)}%` : '—'} · ${games} ${t('leaderboard.games')}`;
    return td;
}

/** Блок «Винрейт по числу игроков»: строки — расы, столбцы — размер лобби. */
function buildPlayersCountSection(rows: RaceByPlayersRow[], baseline: Map<string, number>): HTMLElement {
    const section = document.createElement('section');
    section.className = 'statistics-section';

    const h2 = document.createElement('h2');
    h2.textContent = t('statistics.players_count_title');
    section.appendChild(h2);

    const counts = [...new Set(rows.flatMap((r) => r.rows.map((x) => x.players)))].sort((a, b) => a - b);
    if (rows.length === 0 || counts.length === 0) {
        const p = document.createElement('p');
        p.className = 'hint';
        p.textContent = t('statistics.empty');
        section.appendChild(p);
        return section;
    }

    const desc = document.createElement('p');
    desc.className = 'statistics-balance-desc';
    desc.textContent = t('statistics.players_count_desc');
    section.appendChild(desc);

    const wrap = document.createElement('div');
    wrap.className = 'leaderboard-table-wrap';

    const table = document.createElement('table');
    table.className = 'leaderboard-table statistics-table';

    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    const thRace = document.createElement('th');
    thRace.textContent = t('statistics.col_race');
    headRow.appendChild(thRace);
    for (const n of counts) {
        const th = document.createElement('th');
        th.textContent = String(n);
        th.title = `${n} ${t('statistics.col_players')}`;
        headRow.appendChild(th);
    }
    thead.appendChild(headRow);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    for (const race of rows) {
        const tr = document.createElement('tr');
        tr.appendChild(buildRaceCell(race.race));
        for (const n of counts) {
            const row = race.rows.find((x) => x.players === n);
            if (!row) {
                const td = document.createElement('td');
                td.dataset.label = String(n);
                td.textContent = '—';
                tr.appendChild(td);
                continue;
            }
            tr.appendChild(makeMatrixTd(row.winrate, row.games, String(n), race.race, baseline.get(race.race)));
        }
        tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    wrap.appendChild(table);
    section.appendChild(wrap);
    return section;
}

/** Блок «Своя раса в лобби»: винрейт, когда своей расы 1 / 2 / 3+. */
function buildLobbyRaceSection(rows: LobbyRaceRow[], baseline: Map<string, number>): HTMLElement {
    const section = document.createElement('section');
    section.className = 'statistics-section';

    const h2 = document.createElement('h2');
    h2.textContent = t('statistics.lobby_race_title');
    section.appendChild(h2);

    if (rows.length === 0) {
        const p = document.createElement('p');
        p.className = 'hint';
        p.textContent = t('statistics.empty');
        section.appendChild(p);
        return section;
    }

    const desc = document.createElement('p');
    desc.className = 'statistics-balance-desc';
    desc.textContent = t('statistics.lobby_race_desc');
    section.appendChild(desc);

    const bucketLabels: Record<LobbyBucketRow['key'], TranslationKey> = {
        '1': 'statistics.lobby_bucket_1',
        '2': 'statistics.lobby_bucket_2',
        '3': 'statistics.lobby_bucket_3',
    };

    const wrap = document.createElement('div');
    wrap.className = 'leaderboard-table-wrap';

    const table = document.createElement('table');
    table.className = 'leaderboard-table statistics-table';

    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    const thRace = document.createElement('th');
    thRace.textContent = t('statistics.col_race');
    headRow.appendChild(thRace);
    for (const key of ['1', '2', '3'] as const) {
        const th = document.createElement('th');
        th.textContent = t(bucketLabels[key]);
        headRow.appendChild(th);
    }
    thead.appendChild(headRow);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    for (const race of rows) {
        const tr = document.createElement('tr');
        tr.appendChild(buildRaceCell(race.race));
        for (const key of ['1', '2', '3'] as const) {
            const b = race.buckets.find((x) => x.key === key);
            if (!b || b.games === 0) {
                const td = document.createElement('td');
                td.dataset.label = t(bucketLabels[key]);
                td.textContent = '—';
                tr.appendChild(td);
                continue;
            }
            tr.appendChild(makeMatrixTd(b.winrate, b.games, t(bucketLabels[key]), race.race, baseline.get(race.race)));
        }
        tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    wrap.appendChild(table);
    section.appendChild(wrap);
    return section;
}

/** Цвета мест для стопок: золото → бронза → серый. */
const PLACE_COLORS = ['#f1c40f', '#cfd8dc', '#cd7f32', '#8d6e63', '#5d6d7e', '#46545f', '#333f48', '#26303a'];
const placeColor = (place: number): string => PLACE_COLORS[place - 1] ?? '#26303a';

/** Блок «Распределение мест»: 100% стопки по местам для каждой расы. */
function buildPlaceDistSection(rows: PlaceShareRow[]): HTMLElement {
    const section = document.createElement('section');
    section.className = 'statistics-section';

    const h2 = document.createElement('h2');
    h2.textContent = t('statistics.places_title');
    section.appendChild(h2);

    if (rows.length === 0) {
        const p = document.createElement('p');
        p.className = 'hint';
        p.textContent = t('statistics.empty');
        section.appendChild(p);
        return section;
    }

    const desc = document.createElement('p');
    desc.className = 'statistics-balance-desc';
    desc.textContent = t('statistics.places_desc');
    section.appendChild(desc);

    const places = [...rows].sort((a, b) => a.place - b.place).map((r) => r.place);
    const wrap = document.createElement('div');
    wrap.className = 'statistics-place-bars';

    for (const race of RACES) {
        const rowEl = document.createElement('div');
        rowEl.className = 'statistics-place-row';

        const label = buildRaceTag(race);
        label.classList.add('statistics-place-race');
        rowEl.appendChild(label);

        const track = document.createElement('div');
        track.className = 'statistics-place-track';

        for (const place of places) {
            const value = rows.find((r) => r.place === place)?.[race as 'T' | 'Z' | 'P'] ?? null;
            if (value === null || value <= 0) continue;
            const seg = document.createElement('span');
            seg.className = 'statistics-place-seg';
            if (place >= 4) seg.classList.add('is-dark');
            seg.style.flexBasis = `${value}%`;
            seg.style.background = placeColor(place);
            if (value >= 8) seg.textContent = `#${place}`;
            seg.title = `#${place} — ${value.toFixed(1)}%`;
            track.appendChild(seg);
        }

        rowEl.appendChild(track);
        wrap.appendChild(rowEl);
    }

    // Легенда мест.
    const legend = document.createElement('div');
    legend.className = 'statistics-place-legend';
    for (const place of places) {
        const item = document.createElement('span');
        item.className = 'statistics-place-legend-item';
        const swatch = document.createElement('span');
        swatch.className = 'statistics-place-legend-swatch';
        swatch.style.background = placeColor(place);
        const text = document.createElement('span');
        text.textContent = `#${place}`;
        item.append(swatch, text);
        legend.appendChild(item);
    }

    section.append(wrap, legend);
    return section;
}

/** Блок «Выбывания»: как часто раса выбывает первой и доживает до финала. */
function buildFirstElimSection(rows: FirstElimRow[]): HTMLElement {
    const section = document.createElement('section');
    section.className = 'statistics-section';

    const h2 = document.createElement('h2');
    h2.textContent = t('statistics.first_elim_title');
    section.appendChild(h2);

    if (rows.length === 0) {
        const p = document.createElement('p');
        p.className = 'hint';
        p.textContent = t('statistics.empty');
        section.appendChild(p);
        return section;
    }

    const desc = document.createElement('p');
    desc.className = 'statistics-balance-desc';
    desc.textContent = t('statistics.first_elim_desc');
    section.appendChild(desc);

    const cards = document.createElement('div');
    cards.className = 'statistics-elim-cards';

    for (const r of rows) {
        const card = document.createElement('div');
        card.className = 'statistics-elim-card';

        const head = document.createElement('div');
        head.className = 'statistics-elim-head';
        const who = buildRaceTag(r.race);
        who.classList.add('statistics-elim-race');
        const games = document.createElement('span');
        games.className = 'statistics-elim-games';
        games.textContent = `${r.games} ${t('leaderboard.games')}`;
        head.append(who, games);

        card.append(
            head,
            buildMetricBar(t('statistics.first_elim_first'), `${r.first_share.toFixed(1)}%`, r.first_share / 100, 'is-bad'),
            buildMetricBar(t('statistics.first_elim_last'), `${r.last_share.toFixed(1)}%`, r.last_share / 100, 'is-good')
        );

        cards.appendChild(card);
    }

    section.appendChild(cards);
    return section;
}

/** Названия дней недели (Пн→Вс) для теплокарты. */
const WEEKDAY_LABELS: Record<'ru' | 'en', string[]> = {
    ru: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
    en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
};

/** Блок «Когда играют»: теплокарта день недели × час (МСК). */
function buildHeatmapSection(heatmap: { cells: number[][]; max: number }): HTMLElement {
    const section = document.createElement('section');
    section.className = 'statistics-section';

    const h2 = document.createElement('h2');
    h2.textContent = t('statistics.heatmap_title');
    section.appendChild(h2);

    const cells = heatmap?.cells ?? [];
    const max = heatmap?.max ?? 0;
    if (cells.length !== 7 || max === 0) {
        const p = document.createElement('p');
        p.className = 'hint';
        p.textContent = t('statistics.empty');
        section.appendChild(p);
        return section;
    }

    const desc = document.createElement('p');
    desc.className = 'statistics-balance-desc';
    desc.textContent = t('statistics.heatmap_desc');
    section.appendChild(desc);

    const locale: 'ru' | 'en' = getLocale() === 'en' ? 'en' : 'ru';
    const days = WEEKDAY_LABELS[locale];
    // Ось часов начинается с полудня: 12…23, 00…11 — так 00 оказывается
    // в середине, а вечер и ночь идут непрерывно.
    const hourOrder = [...Array.from({ length: 12 }, (_, i) => i + 12), ...Array.from({ length: 12 }, (_, i) => i)];

    const scroll = document.createElement('div');
    scroll.className = 'statistics-heatmap-scroll';

    const grid = document.createElement('div');
    grid.className = 'statistics-heatmap';

    // Шапка: угол + часы.
    const corner = document.createElement('span');
    corner.className = 'statistics-heatmap-corner';
    grid.appendChild(corner);
    for (const h of hourOrder) {
        const label = document.createElement('span');
        label.className = 'statistics-heatmap-hour';
        label.textContent = String(h).padStart(2, '0');
        grid.appendChild(label);
    }

    // Строки дней.
    for (let d = 0; d < 7; d++) {
        const dayLabel = document.createElement('span');
        dayLabel.className = 'statistics-heatmap-day';
        dayLabel.textContent = days[d]!;
        grid.appendChild(dayLabel);

        for (const h of hourOrder) {
            const v = cells[d]?.[h] ?? 0;
            const cell = document.createElement('span');
            cell.className = 'statistics-heatmap-cell';
            if (v > 0) {
                const intensity = 0.15 + 0.85 * (v / max);
                cell.style.background = `rgba(241, 196, 15, ${intensity.toFixed(3)})`;
            }
            cell.title = `${days[d]} ${String(h).padStart(2, '0')}:00 · ${v} ${t('leaderboard.games')}`;
            grid.appendChild(cell);
        }
    }

    scroll.appendChild(grid);

    const legend = document.createElement('div');
    legend.className = 'statistics-heatmap-legend';
    const less = document.createElement('span');
    less.textContent = t('statistics.heatmap_less');
    const scale = document.createElement('span');
    scale.className = 'statistics-heatmap-scale';
    const more = document.createElement('span');
    more.textContent = t('statistics.heatmap_more');
    legend.append(less, scale, more);

    section.append(scroll, legend);
    return section;
}

// ============================================================
// Переключатель режима «За всё время» / «Сейчас»
// ============================================================
function buildModeToggle(
    active: BalanceMode,
    onChange: (mode: BalanceMode) => void,
    order: BalanceMode[] = ['now', 'all'],
): HTMLElement {
    const box = document.createElement('div');
    box.className = 'statistics-mode-toggle';

    const items: Array<{ mode: BalanceMode; key: TranslationKey }> = order.map((mode) => ({
        mode,
        key: mode === 'now' ? 'statistics.mode_now' : 'statistics.mode_all',
    }));

    for (const item of items) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `statistics-mode-btn${active === item.mode ? ' is-active' : ''}`;
        btn.textContent = t(item.key);
        btn.addEventListener('click', () => {
            if (active === item.mode) return;
            onChange(item.mode);
        });
        box.appendChild(btn);
    }

    return box;
}

// ============================================================
// Блок «Баланс рас»: коэффициент эффективности vs ожидаемых побед
// ============================================================
/** Индекс силы расы в выбранном режиме («за всё время» / «сейчас»). */
function balanceIndex(r: RaceGameRow, mode: BalanceMode): number | null {
    return mode === 'now' ? r.power_index_recent : r.power_index;
}

function buildBalanceSection(rows: RaceGameRow[]): HTMLElement {
    const section = document.createElement('section');
    section.className = 'statistics-section';

    const h2 = document.createElement('h2');
    h2.textContent = t('statistics.balance_title');
    section.appendChild(h2);

    // В балансе «сейчас» — первый и выбранный по умолчанию вариант.
    section.appendChild(buildModeToggle(balanceMode, (mode) => {
        balanceMode = mode;
        rerenderStatistics();
    }, ['now', 'all']));

    const valid = rows.filter((r) => balanceIndex(r, balanceMode) !== null && r.games > 0);
    if (valid.length === 0) {
        const p = document.createElement('p');
        p.className = 'hint';
        p.textContent = t('statistics.empty');
        section.appendChild(p);
        return section;
    }

    const desc = document.createElement('p');
    desc.className = 'statistics-balance-desc';
    desc.textContent = t(balanceMode === 'now' ? 'statistics.balance_desc_now' : 'statistics.balance_desc');
    section.appendChild(desc);

    const sorted = [...valid].sort(
        (a, b) => (balanceIndex(b, balanceMode) as number) - (balanceIndex(a, balanceMode) as number)
    );
    const most = sorted[0];
    const least = sorted[sorted.length - 1];
    if (!most || !least) return section;

    // Карточки-вердикты: самая имбовая / самая слабая раса.
    const verdicts = document.createElement('div');
    verdicts.className = 'balance-verdicts';
    verdicts.appendChild(buildVerdict('imba', t('statistics.balance_most_imba'), most, balanceIndex(most, balanceMode) as number));
    if (least.race !== most.race) {
        verdicts.appendChild(buildVerdict('weak', t('statistics.balance_least_imba'), least, balanceIndex(least, balanceMode) as number));
    }
    section.appendChild(verdicts);

    // Бар-чарт: центр на 1.00 (средний уровень поля).
    const chart = document.createElement('div');
    chart.className = 'balance-chart';

    // Масштаб — по самой «выбивающейся» расе.
    const maxDev = Math.max(
        ...valid.map((r) => Math.abs((balanceIndex(r, balanceMode) as number) - 1)),
        0.01
    );

    for (const r of valid) {
        chart.appendChild(buildBalanceRow(r, balanceIndex(r, balanceMode) as number, maxDev));
    }

    // Подписи-ориентиры: слабее ← 1.00 → сильнее.
    const legend = document.createElement('div');
    legend.className = 'balance-legend';
    legend.innerHTML = `
        <span class="balance-legend-side">${t('statistics.balance_weak_side')}</span>
        <span class="balance-legend-center">1.00 · ${t('statistics.balance_balanced')}</span>
        <span class="balance-legend-side">${t('statistics.balance_imba_side')}</span>
    `;
    chart.appendChild(legend);

    section.appendChild(chart);
    return section;
}

function buildVerdict(kind: 'imba' | 'weak', label: string, r: RaceGameRow, index: number): HTMLElement {
    const card = document.createElement('div');
    card.className = `balance-verdict balance-verdict--${kind}`;

    const lbl = document.createElement('span');
    lbl.className = 'balance-verdict-label';
    lbl.textContent = label;

    const who = document.createElement('span');
    who.className = `balance-verdict-race race-${r.race}`;
    const img = document.createElement('img');
    img.src = RACE_ICON[r.race as Race];
    img.alt = r.race;
    img.className = 'statistics-race-icon';
    const name = document.createElement('span');
    name.textContent = RACE_LABELS[r.race as Race];
    who.append(img, name);

    const val = document.createElement('span');
    val.className = 'balance-verdict-value';
    val.textContent = `×${index.toFixed(2)}`;

    card.append(lbl, who, val);
    return card;
}

function buildBalanceRow(r: RaceGameRow, index: number, maxDev: number): HTMLElement {
    const ratio = index;
    const dev = ratio - 1;
    const widthPct = Math.min(48, (Math.abs(dev) / maxDev) * 48);

    const row = document.createElement('div');
    row.className = 'balance-row';

    const who = document.createElement('span');
    who.className = `balance-row-race race-${r.race}`;
    const img = document.createElement('img');
    img.src = RACE_ICON[r.race as Race];
    img.alt = r.race;
    img.className = 'statistics-race-icon';
    const name = document.createElement('span');
    name.textContent = RACE_LABELS[r.race as Race];
    who.append(img, name);

    const track = document.createElement('div');
    track.className = 'balance-track';

    // Центральная линия среднего уровня.
    const centerLine = document.createElement('span');
    centerLine.className = 'balance-center-line';
    track.appendChild(centerLine);

    const bar = document.createElement('span');
    bar.className = `balance-bar ${dev >= 0 ? 'is-imba' : 'is-weak'} race-${r.race}`;
    bar.style.width = `${widthPct}%`;
    if (dev >= 0) {
        bar.style.left = '50%';
    } else {
        bar.style.right = '50%';
    }
    bar.title = `${r.winrate.toFixed(1)}% · ${t('leaderboard.avg_place')} ${r.avg_place !== null ? r.avg_place.toFixed(2) : '—'}`;
    track.appendChild(bar);

    const val = document.createElement('span');
    val.className = `balance-row-value ${dev >= 0 ? 'is-imba' : 'is-weak'}`;
    val.textContent = `×${ratio.toFixed(2)}`;

    row.append(who, track, val);
    return row;
}

// ============================================================
// Блоки-графики рас во времени: «ММР рас» и «Имба рас»
// ============================================================
const SVG_NS = 'http://www.w3.org/2000/svg';
const RACE_SERIES = ['T', 'Z', 'P'] as const;
type RaceSeriesKey = typeof RACE_SERIES[number];
const RACE_SERIES_COLORS: Record<RaceSeriesKey, string> = {
    T: '#3498db', // синий (как раса Terran)
    Z: '#9b59b6', // фиолетовый (Zerg)
    P: '#f1c40f', // жёлтый (Protoss)
};

interface RaceChartOpts {
    title: string;
    desc: string;
    points: RaceSeriesPoint[];
    /** Точка отсчёта: пунктир и отклонение в легенде. null — без них. */
    base: number | null;
    /** Формат значения в легенде. */
    fmtValue: (v: number) => string;
    /** Формат отклонения от точки отсчёта в легенде (если задан base). */
    fmtDelta?: (v: number) => string;
    /** Формат подписей по оси Y. */
    fmtAxis: (v: number) => string;
    /** Формат подписей по оси X (по умолчанию — дата). */
    fmtX?: (t: string) => string;
    /** Свёрнут по умолчанию, раскрывается кликом по заголовку. */
    collapsible?: boolean;
    /** Состояние раскрытия (переживает перерисовку секции). */
    collapseState?: CollapsibleState;
}

/** Состояние «свёрнуто/раскрыто» для блоков-графиков. */
interface CollapsibleState {
    open: boolean;
}

const powerCollapseState: CollapsibleState = { open: false };
const mmrCollapseState: CollapsibleState = { open: false };

/** Блок «ММР рас»: кумулятивный условный рейтинг T/Z/P во времени. */
function buildRaceMmrSection(data: { base: number; points: RaceSeriesPoint[] }): HTMLElement {
    return buildRaceChartSection({
        title: t('statistics.mmr_title'),
        desc: t('statistics.mmr_desc'),
        base: data?.base ?? 1500,
        points: data?.points ?? [],
        fmtValue: (v) => v.toFixed(0),
        fmtDelta: (v) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}`,
        fmtAxis: (v) => v.toFixed(0),
        collapsible: true,
        collapseState: mmrCollapseState,
    });
}

/** Блок «Имба рас»: индекс силы T/Z/P во времени (1.00 — средний уровень поля). */
function buildRacePowerSection(allPoints: RaceSeriesPoint[], recentPoints: RaceSeriesPoint[]): HTMLElement {
    const section = document.createElement('section');
    section.className = 'statistics-section';

    const h2 = document.createElement('h2');
    h2.textContent = t('statistics.power_title');

    const points = (powerMode === 'now' ? recentPoints : allPoints) ?? [];
    if (points.length === 0) {
        section.appendChild(h2);
        const p = document.createElement('p');
        p.className = 'hint';
        p.textContent = t('statistics.empty');
        section.appendChild(p);
        return section;
    }

    const body = document.createElement('div');

    body.appendChild(buildModeToggle(powerMode, (mode) => {
        powerMode = mode;
        rerenderStatistics();
    }));

    const desc = document.createElement('p');
    desc.className = 'statistics-balance-desc';
    desc.textContent = t(powerMode === 'now' ? 'statistics.power_desc_now' : 'statistics.power_desc');
    body.appendChild(desc);

    body.appendChild(buildRaceLineChart({
        title: '',
        desc: '',
        base: 1,
        points,
        fmtValue: (v) => `×${v.toFixed(2)}`,
        fmtDelta: (v) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}`,
        fmtAxis: (v) => v.toFixed(2),
    }));

    attachCollapsible(section, h2, body, powerCollapseState);
    return section;
}

/** Подпись месяца из строки YYYY-MM: «ММ.ГГ». */
function fmtMonthShort(month: string): string {
    const [y, m] = month.split('-');
    if (!y || !m) return month;
    return `${m}.${y.slice(2)}`;
}

/** Блок «Игроки рас по месяцам»: сколько уникальных игроков каждой расы играло за месяц. */
function buildRacePlayersSection(rows: MonthRacePlayersRow[]): HTMLElement {
    const points: RaceSeriesPoint[] = (rows ?? []).map((r) => ({ t: r.month, T: r.T, Z: r.Z, P: r.P }));
    return buildRaceChartSection({
        title: t('statistics.race_players_title'),
        desc: t('statistics.race_players_desc'),
        base: null,
        points,
        fmtValue: (v) => String(Math.round(v)),
        fmtAxis: (v) => v.toFixed(0),
        fmtX: fmtMonthShort,
    });
}

function buildRaceChartSection(opts: RaceChartOpts): HTMLElement {
    const section = document.createElement('section');
    section.className = 'statistics-section';

    const h2 = document.createElement('h2');
    h2.textContent = opts.title;

    if (opts.points.length === 0) {
        section.appendChild(h2);
        const p = document.createElement('p');
        p.className = 'hint';
        p.textContent = t('statistics.empty');
        section.appendChild(p);
        return section;
    }

    const body = document.createElement('div');

    const desc = document.createElement('p');
    desc.className = 'statistics-balance-desc';
    desc.textContent = opts.desc;
    body.appendChild(desc);

    body.appendChild(buildRaceLineChart(opts));

    if (opts.collapsible) {
        attachCollapsible(section, h2, body, opts.collapseState);
    } else {
        section.append(h2, body);
    }
    return section;
}

/**
 * Делает секцию сворачиваемой: заголовок — кнопка, тело плавно
 * раскрывается (grid `0fr → 1fr`). По умолчанию свёрнута.
 */
function attachCollapsible(
    section: HTMLElement,
    h2: HTMLElement,
    body: HTMLElement,
    state: CollapsibleState = { open: false }
): void {
    h2.classList.add('statistics-collapsible-title');
    h2.setAttribute('role', 'button');
    h2.tabIndex = 0;
    h2.setAttribute('aria-expanded', String(state.open));

    const wrap = document.createElement('div');
    wrap.className = 'statistics-collapsible';
    if (state.open) wrap.classList.add('is-open');
    wrap.appendChild(body);

    const toggle = (): void => {
        state.open = !state.open;
        wrap.classList.toggle('is-open', state.open);
        h2.setAttribute('aria-expanded', String(state.open));
    };
    h2.addEventListener('click', toggle);
    h2.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
        }
    });

    section.append(h2, wrap);
}

/** Короткий формат даты для оси X: ДД.ММ.ГГ. */
function fmtDateShort(d: Date): string {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}.${mm}.${yy}`;
}

/** Общий SVG-график трёх линий рас (значения могут быть null — линия рвётся). */
function buildRaceLineChart(opts: RaceChartOpts): HTMLElement {
    const { base, points, fmtValue, fmtDelta, fmtAxis } = opts;
    const n = points.length;

    // На телефоне viewBox уже — иначе подписи сжимаются в нечитаемую кашу.
    const narrow = typeof window !== 'undefined' && window.innerWidth <= 700;
    const W = narrow ? 340 : 900;
    const H = narrow ? 220 : 250;
    const padL = narrow ? 42 : 50;
    const padR = narrow ? 12 : 18;
    const padT = 16;
    const padB = narrow ? 28 : 34;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;

    const wrap = document.createElement('div');
    wrap.className = 'statistics-mmr-chart';

    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.classList.add('lb-chart-svg');

    // Диапазон Y: все непустые значения + точка отсчёта (если задана), с отступом.
    const allValues: number[] = base !== null ? [base] : [];
    for (const p of points) {
        for (const race of RACE_SERIES) {
            const v = p[race];
            if (v !== null) allValues.push(v);
        }
    }
    let min = allValues.length ? Math.min(...allValues) : 0;
    let max = allValues.length ? Math.max(...allValues) : 1;
    if (max - min < 1e-6) {
        min -= 1;
        max += 1;
    }
    const pad = base === 1 ? 0.04 : Math.max((max - min) * 0.1, 1);
    min -= pad;
    max += pad;
    const range = max - min || 1;

    const xOf = (i: number): number => padL + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW);
    const yOf = (v: number): number => padT + plotH - ((v - min) / range) * plotH;

    // Горизонтальная сетка + подписи значений по Y.
    for (let gi = 0; gi <= 4; gi++) {
        const y = padT + (gi / 4) * plotH;
        const line = document.createElementNS(SVG_NS, 'line');
        line.setAttribute('x1', String(padL));
        line.setAttribute('y1', y.toFixed(2));
        line.setAttribute('x2', String(W - padR));
        line.setAttribute('y2', y.toFixed(2));
        line.classList.add('lb-chart-grid');
        svg.appendChild(line);

        const label = document.createElementNS(SVG_NS, 'text');
        label.setAttribute('x', String(padL - 8));
        label.setAttribute('y', (y + 4).toFixed(2));
        label.setAttribute('text-anchor', 'end');
        label.classList.add('lb-chart-xlabel');
        label.textContent = fmtAxis(max - (gi / 4) * range);
        svg.appendChild(label);
    }

    // Пунктирная линия точки отсчёта (если задана).
    if (base !== null) {
        const baseY = yOf(base);
        const baseLine = document.createElementNS(SVG_NS, 'line');
        baseLine.setAttribute('x1', String(padL));
        baseLine.setAttribute('y1', baseY.toFixed(2));
        baseLine.setAttribute('x2', String(W - padR));
        baseLine.setAttribute('y2', baseY.toFixed(2));
        baseLine.classList.add('mmr-baseline');
        svg.appendChild(baseLine);
    }

    // Индексы последних непустых значений (для точки на графике и легенды).
    const lastIndex: Record<RaceSeriesKey, number> = { T: -1, Z: -1, P: -1 };
    for (const race of RACE_SERIES) {
        for (let i = n - 1; i >= 0; i--) {
            if (points[i]![race] !== null) {
                lastIndex[race] = i;
                break;
            }
        }
    }

    // Линии рас: null-значения разрывают линию на отдельные сегменты.
    for (const race of RACE_SERIES) {
        let seg: string[] = [];
        const flush = (): void => {
            if (!seg.length) return;
            const poly = document.createElementNS(SVG_NS, 'polyline');
            poly.setAttribute('points', seg.join(' '));
            poly.setAttribute('fill', 'none');
            poly.setAttribute('stroke', RACE_SERIES_COLORS[race]);
            poly.setAttribute('stroke-width', '2');
            poly.setAttribute('stroke-linejoin', 'round');
            poly.classList.add('lb-chart-line');
            svg.appendChild(poly);
            seg = [];
        };
        points.forEach((p, i) => {
            const v = p[race];
            if (v === null) {
                flush();
                return;
            }
            seg.push(`${xOf(i).toFixed(2)},${yOf(v).toFixed(2)}`);
        });
        flush();

        // Точка на конце линии — текущее значение.
        const li = lastIndex[race];
        if (li >= 0) {
            const v = points[li]![race] as number;
            const dot = document.createElementNS(SVG_NS, 'circle');
            dot.setAttribute('cx', xOf(li).toFixed(2));
            dot.setAttribute('cy', yOf(v).toFixed(2));
            dot.setAttribute('r', '3.5');
            dot.setAttribute('fill', RACE_SERIES_COLORS[race]);
            dot.setAttribute('stroke', '#0a0c10');
            dot.setAttribute('stroke-width', '1.5');
            svg.appendChild(dot);
        }
    }

    // Ось X: несколько равномерных подписей (по умолчанию — даты, для месяцев — fmtX).
    const fmtX = opts.fmtX ?? ((iso: string): string => {
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? '' : fmtDateShort(d);
    });
    const labelIdx: number[] = [];
    const step = Math.max(1, Math.ceil(n / (narrow ? 4 : 8)));
    for (let i = 0; i < n; i += step) labelIdx.push(i);
    if (labelIdx.length && labelIdx[labelIdx.length - 1] !== n - 1) labelIdx.push(n - 1);

    for (const i of labelIdx) {
        const text = fmtX(points[i]?.t ?? '');
        if (!text) continue;
        const txt = document.createElementNS(SVG_NS, 'text');
        txt.setAttribute('x', xOf(i).toFixed(2));
        txt.setAttribute('y', String(H - padB + 16));
        txt.setAttribute('text-anchor', i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle');
        txt.classList.add('lb-chart-xlabel');
        txt.textContent = text;
        svg.appendChild(txt);
    }

    // Наведение: вертикальный прицел, точки на линиях и тултип со значениями.
    const crosshair = document.createElementNS(SVG_NS, 'line');
    crosshair.setAttribute('y1', String(padT));
    crosshair.setAttribute('y2', String(H - padB));
    crosshair.setAttribute('x1', '0');
    crosshair.setAttribute('x2', '0');
    crosshair.classList.add('lb-chart-crosshair');
    svg.appendChild(crosshair);

    const hoverDots = {} as Record<RaceSeriesKey, SVGCircleElement>;
    for (const race of RACE_SERIES) {
        const dot = document.createElementNS(SVG_NS, 'circle');
        dot.setAttribute('r', '4');
        dot.setAttribute('fill', RACE_SERIES_COLORS[race]);
        dot.setAttribute('stroke', '#0a0c10');
        dot.setAttribute('stroke-width', '1.5');
        dot.classList.add('statistics-hoverdot');
        dot.style.opacity = '0';
        svg.appendChild(dot);
        hoverDots[race] = dot;
    }

    const tooltip = document.createElement('div');
    tooltip.className = 'lb-chart-tooltip';

    svg.addEventListener('pointermove', (e) => {
        const rect = svg.getBoundingClientRect();
        if (!rect.width) return;
        const frac = (e.clientX - rect.left) / rect.width;
        let idx = Math.round((((frac * W) - padL) / plotW) * (n - 1));
        idx = Math.max(0, Math.min(n - 1, idx));
        const cx = xOf(idx);

        crosshair.setAttribute('x1', cx.toFixed(2));
        crosshair.setAttribute('x2', cx.toFixed(2));
        crosshair.classList.add('is-visible');

        const pt = points[idx]!;
        for (const race of RACE_SERIES) {
            const dot = hoverDots[race];
            const v = pt[race];
            if (v === null) {
                dot.style.opacity = '0';
                continue;
            }
            dot.setAttribute('cx', cx.toFixed(2));
            dot.setAttribute('cy', yOf(v).toFixed(2));
            dot.style.opacity = '1';
        }

        tooltip.innerHTML = '';
        const head = document.createElement('div');
        head.className = 'lb-chart-tt-date';
        head.textContent = fmtX(pt.t);
        tooltip.appendChild(head);
        for (const race of RACE_SERIES) {
            const v = pt[race];
            const row = document.createElement('div');
            row.className = 'lb-chart-tt-row';
            const d = document.createElement('i');
            d.className = 'lb-chart-dot';
            d.style.background = RACE_SERIES_COLORS[race];
            const lab = document.createElement('span');
            lab.className = 'lb-chart-tt-label';
            lab.textContent = RACE_LABELS[race];
            const value = document.createElement('span');
            value.className = 'lb-chart-tt-value';
            value.textContent = v === null ? '—' : fmtValue(v);
            row.append(d, lab, value);
            tooltip.appendChild(row);
        }

        tooltip.style.display = 'block';
        const leftPct = (cx / W) * 100;
        tooltip.style.left = leftPct > 62 ? `${leftPct - 18}%` : `${leftPct + 4}%`;
        tooltip.style.top = '6px';
    });

    svg.addEventListener('pointerleave', () => {
        crosshair.classList.remove('is-visible');
        tooltip.style.display = 'none';
        for (const race of RACE_SERIES) hoverDots[race].style.opacity = '0';
    });

    wrap.appendChild(svg);
    wrap.appendChild(tooltip);

    // Легенда с текущими значениями и отклонением от точки отсчёта.
    const legend = document.createElement('div');
    legend.className = 'statistics-mmr-legend';

    for (const race of RACE_SERIES) {
        const item = document.createElement('div');
        item.className = `statistics-mmr-legend-item race-${race}`;

        const img = document.createElement('img');
        img.src = RACE_ICON[race];
        img.alt = race;
        img.className = 'statistics-race-icon';

        const name = document.createElement('span');
        name.textContent = RACE_LABELS[race];

        const li = lastIndex[race];
        const value = li >= 0 ? (points[li]![race] as number) : null;

        const val = document.createElement('span');
        val.className = 'statistics-mmr-value';
        val.textContent = value === null ? '—' : fmtValue(value);

        const dev = document.createElement('span');
        if (value === null || base === null || !fmtDelta) {
            dev.className = 'statistics-mmr-delta';
            dev.textContent = '';
        } else {
            const delta = value - base;
            dev.className = `statistics-mmr-delta ${delta >= 0 ? 'is-up' : 'is-down'}`;
            dev.textContent = fmtDelta(delta);
        }

        item.append(img, name, val, dev);
        legend.appendChild(item);
    }

    wrap.appendChild(legend);
    return wrap;
}

// ============================================================
// Блок «Игр по месяцам»: столбцы по числу игр за месяц
// ============================================================
function buildGamesByMonthSection(rows: MonthGamesRow[]): HTMLElement {
    const section = document.createElement('section');
    section.className = 'statistics-section';

    const h2 = document.createElement('h2');
    h2.textContent = t('statistics.monthly_games_title');
    section.appendChild(h2);

    const data = rows ?? [];
    if (data.length === 0) {
        const p = document.createElement('p');
        p.className = 'hint';
        p.textContent = t('statistics.empty');
        section.appendChild(p);
        return section;
    }

    const desc = document.createElement('p');
    desc.className = 'statistics-balance-desc';
    desc.textContent = t('statistics.monthly_games_desc');
    section.appendChild(desc);

    section.appendChild(buildBarsChart(data));
    return section;
}

/** Округление вверх до «красивого» предела оси Y (1/2/2.5/5/10 × 10^n). */
function niceCeil(v: number): number {
    if (v <= 5) return 5;
    const pow = Math.pow(10, Math.floor(Math.log10(v)));
    const base = v / pow;
    const mult = base <= 1 ? 1 : base <= 2 ? 2 : base <= 2.5 ? 2.5 : base <= 5 ? 5 : 10;
    return mult * pow;
}

/** Простой SVG-бар-чарт по месяцам. */
function buildBarsChart(rows: MonthGamesRow[]): HTMLElement {
    const n = rows.length;
    const narrow = typeof window !== 'undefined' && window.innerWidth <= 700;
    const W = narrow ? 340 : 900;
    const H = narrow ? 220 : 240;
    const padL = narrow ? 34 : 44;
    const padR = narrow ? 10 : 16;
    const padT = 22;
    const padB = narrow ? 28 : 34;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;

    const wrap = document.createElement('div');
    wrap.className = 'statistics-mmr-chart';

    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.classList.add('lb-chart-svg');

    const niceMax = niceCeil(Math.max(...rows.map((r) => r.games), 1));

    // Сетка + подписи по Y.
    for (let gi = 0; gi <= 4; gi++) {
        const y = padT + (gi / 4) * plotH;
        const line = document.createElementNS(SVG_NS, 'line');
        line.setAttribute('x1', String(padL));
        line.setAttribute('y1', y.toFixed(2));
        line.setAttribute('x2', String(W - padR));
        line.setAttribute('y2', y.toFixed(2));
        line.classList.add('lb-chart-grid');
        svg.appendChild(line);

        const label = document.createElementNS(SVG_NS, 'text');
        label.setAttribute('x', String(padL - 8));
        label.setAttribute('y', (y + 4).toFixed(2));
        label.setAttribute('text-anchor', 'end');
        label.classList.add('lb-chart-xlabel');
        label.textContent = String(Math.round(niceMax - (gi / 4) * niceMax));
        svg.appendChild(label);
    }

    const slot = plotW / n;
    const barW = Math.max(2, Math.min(slot * 0.65, 48));
    const axisY = padT + plotH;
    const showValues = n <= 24 && barW >= 12;

    const bars: SVGRectElement[] = [];
    rows.forEach((r, i) => {
        const x = padL + slot * i + (slot - barW) / 2;
        const h = (r.games / niceMax) * plotH;
        const y = axisY - h;

        const bar = document.createElementNS(SVG_NS, 'rect');
        bar.setAttribute('x', x.toFixed(2));
        bar.setAttribute('y', y.toFixed(2));
        bar.setAttribute('width', barW.toFixed(2));
        bar.setAttribute('height', Math.max(0, h).toFixed(2));
        bar.setAttribute('rx', '2');
        bar.classList.add('statistics-bars-bar');
        svg.appendChild(bar);
        bars.push(bar);

        if (showValues) {
            const val = document.createElementNS(SVG_NS, 'text');
            val.setAttribute('x', (x + barW / 2).toFixed(2));
            val.setAttribute('y', (y - 4).toFixed(2));
            val.classList.add('statistics-bar-value');
            val.textContent = String(r.games);
            svg.appendChild(val);
        }
    });

    // Базовая линия оси X.
    const axis = document.createElementNS(SVG_NS, 'line');
    axis.setAttribute('x1', String(padL));
    axis.setAttribute('y1', axisY.toFixed(2));
    axis.setAttribute('x2', String(W - padR));
    axis.setAttribute('y2', axisY.toFixed(2));
    axis.classList.add('lb-chart-axis');
    svg.appendChild(axis);

    // Подписи месяцев.
    const step = Math.max(1, Math.ceil(n / (narrow ? 5 : 12)));
    for (let i = 0; i < n; i += step) {
        const txt = document.createElementNS(SVG_NS, 'text');
        txt.setAttribute('x', (padL + slot * i + slot / 2).toFixed(2));
        txt.setAttribute('y', String(H - padB + 16));
        txt.setAttribute('text-anchor', 'middle');
        txt.classList.add('lb-chart-xlabel');
        txt.textContent = fmtMonthShort(rows[i]!.month);
        svg.appendChild(txt);
    }

    // Наведение: подсветка столбца и тултип с месяцем и числом игр.
    const tooltip = document.createElement('div');
    tooltip.className = 'lb-chart-tooltip';

    svg.addEventListener('pointermove', (e) => {
        const rect = svg.getBoundingClientRect();
        if (!rect.width) return;
        const plotX = ((e.clientX - rect.left) / rect.width) * W;
        let idx = Math.floor((plotX - padL) / slot);
        idx = Math.max(0, Math.min(n - 1, idx));

        bars.forEach((b, i) => b.classList.toggle('is-active', i === idx));
        const r = rows[idx]!;
        const cx = padL + slot * idx + slot / 2;

        tooltip.innerHTML = '';
        const head = document.createElement('div');
        head.className = 'lb-chart-tt-date';
        head.textContent = fmtMonthShort(r.month);
        tooltip.appendChild(head);

        const row = document.createElement('div');
        row.className = 'lb-chart-tt-row';
        const lab = document.createElement('span');
        lab.className = 'lb-chart-tt-label';
        lab.textContent = t('leaderboard.games');
        const value = document.createElement('span');
        value.className = 'lb-chart-tt-value';
        value.textContent = String(r.games);
        row.append(lab, value);
        tooltip.appendChild(row);

        tooltip.style.display = 'block';
        const leftPct = (cx / W) * 100;
        tooltip.style.left = leftPct > 62 ? `${leftPct - 18}%` : `${leftPct + 4}%`;
        tooltip.style.top = '6px';
    });

    svg.addEventListener('pointerleave', () => {
        tooltip.style.display = 'none';
        bars.forEach((b) => b.classList.remove('is-active'));
    });

    wrap.appendChild(svg);
    wrap.appendChild(tooltip);
    return wrap;
}

// --- Раздел: средние оценки стиля по расам ---
function buildStyleSection(rows: RaceStyleRow[]): HTMLElement {
    const section = document.createElement('section');
    section.className = 'statistics-section';

    const h2 = document.createElement('h2');
    h2.textContent = t('statistics.style_title');
    section.appendChild(h2);

    if (rows.length === 0) {
        const p = document.createElement('p');
        p.className = 'hint';
        p.textContent = t('statistics.empty');
        section.appendChild(p);
        return section;
    }

    const desc = document.createElement('p');
    desc.className = 'statistics-balance-desc';
    desc.textContent = t('statistics.style_desc');
    section.appendChild(desc);

    const cards = document.createElement('div');
    cards.className = 'statistics-style-cards';

    for (const race of RACES) {
        const r = rows.find((x) => x.race === race);
        if (!r) continue;

        const card = document.createElement('div');
        card.className = `statistics-style-card race-${race}`;

        const head = document.createElement('div');
        head.className = 'statistics-style-card-head';
        head.appendChild(buildRaceTag(race));
        const ratings = document.createElement('span');
        ratings.className = 'statistics-style-ratings';
        ratings.textContent = `${fmtInt(r.ratings)} ${t('statistics.col_ratings')}`;
        head.appendChild(ratings);

        // Радар рисуется тем же компонентом, что и карточка игрока.
        const radar = document.createElement('div');
        radar.className = 'statistics-style-radar';
        radar.innerHTML = buildRadarSVG({
            stats: {
                adaptiveness: r.adaptiveness ?? 0,
                aggression: r.aggression ?? 0,
                turtle: r.turtle ?? 0,
                variety: r.variety ?? 0,
                survival: r.survival ?? 0,
                greed: r.greed ?? 0,
            },
            color: RACE_SERIES_COLORS[race as RaceSeriesKey],
            size: 260,
            uniqueId: `stats-style-${race}`,
        });

        card.append(head, radar);
        cards.appendChild(card);
    }

    section.appendChild(cards);
    return section;
}

// ============================================================
// Разделы справочников: карты, моды, ведущие
// ============================================================
function buildRefSection(
    sectionKey: RefSectionKey,
    title: string,
    rows: RefStatRow[],
    expandable: boolean
): HTMLElement {
    const section = document.createElement('section');
    section.className = 'statistics-section';

    const h2 = document.createElement('h2');
    h2.textContent = title;
    section.appendChild(h2);

    if (rows.length === 0) {
        const p = document.createElement('p');
        p.className = 'hint';
        p.textContent = t('statistics.empty');
        section.appendChild(p);
        return section;
    }

    const isHosts = sectionKey === 'hosts';
    const columns: Array<{ key: RefSortKey; label: string; race?: 'T' | 'Z' | 'P' }> = isHosts
        ? [
              { key: 'name', label: t('statistics.col_name') },
              { key: 'games', label: t('leaderboard.games') },
              { key: 'players', label: t('statistics.col_players') },
              { key: 'avg_players', label: t('statistics.col_avg_players') },
              { key: 'avg_duration', label: t('statistics.col_avg_duration') },
              { key: 'last_played_at', label: t('statistics.col_last_game') },
          ]
        : [
              { key: 'name', label: t('statistics.col_name') },
              { key: 'games', label: t('leaderboard.games') },
              { key: 'avg_duration', label: t('statistics.col_avg_duration') },
              // «Карта для скилла»: доля побед игрока с максимальным MMR до игры.
              ...(sectionKey === 'maps'
                  ? ([{ key: 'favorite_winrate', label: t('statistics.col_favorite_win') }] as Array<{
                        key: RefSortKey;
                        label: string;
                    }>)
                  : []),
              { key: 'race_T', label: 'T', race: 'T' },
              { key: 'race_Z', label: 'Z', race: 'Z' },
              { key: 'race_P', label: 'P', race: 'P' },
          ];

    const sorted = sortRef(rows, refSort[sectionKey]);
    const visible = expandable ? sorted.slice(0, REF_VISIBLE) : sorted;
    const extra = expandable ? sorted.slice(REF_VISIBLE) : [];

    const wrap = document.createElement('div');
    wrap.className = 'leaderboard-table-wrap';

    const table = document.createElement('table');
    table.className = 'leaderboard-table statistics-table ref-table';
    table.appendChild(buildColgroup(columns.length));
    table.appendChild(buildRefHead(sectionKey, columns));
    table.appendChild(buildRefBody(sectionKey, visible, columns));
    wrap.appendChild(table);

    if (extra.length > 0 && (sectionKey === 'maps' || sectionKey === 'mods')) {
        const detailsWrap = document.createElement('div');
        detailsWrap.className = 'statistics-ref-details';
        if (refExpanded[sectionKey]) detailsWrap.classList.add('is-open');

        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'statistics-ref-toggle';

        const extraWrap = document.createElement('div');
        extraWrap.className = 'statistics-ref-expandable';

        // Продолжение таблицы без шапки; ширины колонок заданы colgroup —
        // поэтому строки выравниваются с основной таблицей.
        const extraTable = document.createElement('table');
        extraTable.className = 'leaderboard-table statistics-table ref-table ref-table-extra';
        extraTable.appendChild(buildColgroup(columns.length));
        extraTable.appendChild(buildRefBody(sectionKey, extra, columns));
        extraWrap.appendChild(extraTable);

        const updateToggle = (): void => {
            const open = detailsWrap.classList.contains('is-open');
            toggle.textContent = open
                ? t('statistics.hide_all')
                : `${t('statistics.show_all')} (${sorted.length})`;
        };
        updateToggle();

        toggle.addEventListener('click', () => {
            const open = detailsWrap.classList.toggle('is-open');
            refExpanded[sectionKey] = open;
            updateToggle();
        });

        detailsWrap.append(toggle, extraWrap);
        wrap.appendChild(detailsWrap);
    }

    section.appendChild(wrap);
    return section;
}

function buildColgroup(count: number): HTMLTableColElement {
    const colgroup = document.createElement('colgroup');
    // Первая колонка (название) шире, остальные равны — общая ширина 100%.
    const firstW = count <= 5 ? 40 : 35;
    const restW = count > 1 ? (100 - firstW) / (count - 1) : 0;
    for (let i = 0; i < count; i++) {
        const col = document.createElement('col');
        col.style.width = `${i === 0 ? firstW : restW}%`;
        colgroup.appendChild(col);
    }
    return colgroup;
}

function buildRefHead(
    sectionKey: RefSectionKey,
    columns: Array<{ key: RefSortKey; label: string; race?: 'T' | 'Z' | 'P' }>
): HTMLTableSectionElement {
    const thead = document.createElement('thead');
    const raceCols = columns.filter((c) => c.race);

    // Ведущие — обычная одноуровневая шапка.
    if (raceCols.length === 0) {
        const row = document.createElement('tr');
        for (const col of columns) row.appendChild(makeRefHeaderTh(sectionKey, col, false));
        thead.appendChild(row);
        return thead;
    }

    // Карты/моды — сгруппированная шапка: общий столбец «Винрейт» над
    // под-колонками рас; остальные колонки объединены по вертикали.
    const row1 = document.createElement('tr');
    for (const col of columns) {
        if (col.race) continue;
        row1.appendChild(makeRefHeaderTh(sectionKey, col, true));
    }
    const thWR = document.createElement('th');
    thWR.colSpan = raceCols.length;
    thWR.className = 'statistics-group-title';
    thWR.textContent = t('leaderboard.winrate');
    row1.appendChild(thWR);
    thead.appendChild(row1);

    const row2 = document.createElement('tr');
    for (const col of raceCols) row2.appendChild(makeRefHeaderTh(sectionKey, col, false));
    thead.appendChild(row2);

    return thead;
}

/** Сортируемая ячейка шапки справочника (строка или иконка расы). */
function makeRefHeaderTh(
    sectionKey: RefSectionKey,
    col: { key: RefSortKey; label: string; race?: 'T' | 'Z' | 'P' },
    rowSpan2: boolean
): HTMLTableCellElement {
    const isText = col.key === 'name' || col.key === 'last_played_at';
    const label: string | HTMLElement = col.race ? makeRaceHeaderIcon(col.race) : col.label;
    const th = buildSortableTh(label, refSort[sectionKey].key === col.key, refSort[sectionKey].dir, () => {
        handleSort(refSort[sectionKey], col.key, isText, (next) => {
            refSort[sectionKey] = next;
            if (cache) renderStatistics(cache, document.getElementById('statistics-container')!);
        });
    });
    if (col.race) th.classList.add(`race-${col.race}`);
    if (rowSpan2) th.rowSpan = 2;
    return th;
}

/** Иконка расы для заголовка колонки винрейта. */
function makeRaceHeaderIcon(race: 'T' | 'Z' | 'P'): HTMLImageElement {
    const img = document.createElement('img');
    img.src = RACE_ICON[race];
    img.alt = RACE_LABELS[race];
    img.title = RACE_LABELS[race];
    img.className = 'statistics-ref-race-icon';
    return img;
}

function buildRefBody(
    sectionKey: RefSectionKey,
    rows: RefStatRow[],
    columns: Array<{ key: RefSortKey; label: string; race?: 'T' | 'Z' | 'P' }>
): HTMLTableSectionElement {
    const tbody = document.createElement('tbody');
    for (const r of rows) {
        const tr = document.createElement('tr');
        for (const col of columns) {
            const td = document.createElement('td');
            if (col.key === 'name') {
                td.className = 'statistics-ref-name';
                td.appendChild(buildRefNameCell(sectionKey, r));
            } else {
                td.dataset.label = col.label;
                if (col.race) td.className = `statistics-race-win race-${col.race}`;

                // Винрейт расы и «победы фаворита» — полоса в ячейке, число поверх.
                if (col.race || col.key === 'favorite_winrate') {
                    td.classList.add('statistics-bar-cell');
                    const raw = col.race ? r.race_winrate[col.race] : r.favorite_winrate;
                    if (raw !== null) {
                        const bar = document.createElement('span');
                        bar.className = 'statistics-cell-bar';
                        if (!col.race) bar.classList.add('is-gold');
                        bar.style.width = `${Math.max(0, Math.min(100, raw))}%`;
                        td.appendChild(bar);
                    }
                    const value = document.createElement('span');
                    value.className = 'statistics-cell-value';
                    value.textContent = refCellValue(r, col.key);
                    td.appendChild(value);

                    if (col.key === 'favorite_winrate') {
                        td.classList.add('statistics-stat');
                        td.title = `${r.rated_games} ${t('leaderboard.games')}`;
                    }
                } else {
                    td.textContent = refCellValue(r, col.key);
                }
            }
            tr.appendChild(td);
        }
        tbody.appendChild(tr);
    }
    return tbody;
}

/**
 * Ячейка названия справочника: ведущий → страница игрока (если привязан),
 * карта/мод → фильтр в списке игр.
 */
function buildRefNameCell(sectionKey: RefSectionKey, r: RefStatRow): HTMLElement {
    let href: string | null = null;
    if (sectionKey === 'hosts') {
        if (r.player_name) href = `/?player=${encodeURIComponent(r.player_name)}`;
    } else if (sectionKey === 'maps') {
        if (r.id !== null) href = `/games?map=${r.id}`;
    } else if (sectionKey === 'mods') {
        href = `/games?mod=${encodeURIComponent(r.name)}`;
    }

    if (!href) {
        const span = document.createElement('span');
        span.textContent = r.name;
        span.title = r.name;
        return span;
    }

    const link = document.createElement('a');
    link.className = 'statistics-ref-link';
    link.href = href;
    link.textContent = r.name;
    link.title = r.name;
    link.addEventListener('click', (e) => {
        e.preventDefault();
        navigateTo(href!);
    });
    return link;
}

function refCellValue(r: RefStatRow, key: RefSortKey): string {
    switch (key) {
        case 'name': return r.name;
        case 'games': return String(r.games);
        case 'players': return String(r.players);
        case 'avg_players': return r.avg_players.toFixed(1);
        case 'avg_duration': return r.avg_duration !== null ? r.avg_duration.toFixed(1) : '—';
        case 'last_played_at': return r.last_played_at ? formatDate(r.last_played_at) : '—';
        case 'race_T': return fmtPct(r.race_winrate.T);
        case 'race_Z': return fmtPct(r.race_winrate.Z);
        case 'race_P': return fmtPct(r.race_winrate.P);
        case 'favorite_winrate': return fmtPct(r.favorite_winrate);
    }
}

function fmtPct(v: number | null): string {
    return v !== null ? `${v.toFixed(1)}%` : '—';
}

/** Значение для сортировки справочника (расы — вложены в race_winrate). */
function refSortValue(r: RefStatRow, key: RefSortKey): number | string | null {
    switch (key) {
        case 'race_T': return r.race_winrate.T;
        case 'race_Z': return r.race_winrate.Z;
        case 'race_P': return r.race_winrate.P;
        case 'favorite_winrate': return r.favorite_winrate;
        default: return (r as any)[key];
    }
}

function sortRef(rows: RefStatRow[], sort: { key: RefSortKey; dir: SortDir }): RefStatRow[] {
    return [...rows].sort((a, b) => compareValues(refSortValue(a, sort.key), refSortValue(b, sort.key), sort.dir));
}

function formatDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}.${d.getFullYear()}`;
}